/**
 * Data layer for `budget-pacing-alerts`. Thin typed fetch wrappers over /api/v1/budget-pacing-alerts.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type Severity = "none" | "warn" | "critical";

export interface BudgetPacingAlertRow {
	id: number;
	tenant_id: string;
	scope_type: string;
	scope_id: string;
	budget_cap: number;
	spent: number;
	period_start: number;
	period_end: number;
	warn_threshold_pct: number;
	crit_threshold_pct: number;
	burn_pct: number | null;
	elapsed_pct: number | null;
	pace_ratio: number | null;
	projected_end_spend: number | null;
	severity: Severity;
	alerted_at: number | null;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListBudgetPacingAlertsResponse {
	items: BudgetPacingAlertRow[];
}

export interface BudgetPacingAlertsListFilters {
	severity?: Severity | "";
	scope_type?: string;
	scope_id?: string;
	sort?: "burn" | "created_at";
}

export interface UpsertBudgetPacingAlertBody {
	scope_type: string;
	scope_id: string;
	budget_cap: number;
	spent: number;
	period_start: number;
	period_end: number;
	warn_threshold_pct?: number;
	crit_threshold_pct?: number;
	now_seconds?: number;
}

export interface UpsertBudgetPacingAlertResponse {
	ok: boolean;
	scope_type: string;
	scope_id: string;
	budget_cap: number;
	spent: number;
	period_start: number;
	period_end: number;
	warn_threshold_pct: number;
	crit_threshold_pct: number;
	burn_pct: number | null;
	elapsed_pct: number | null;
	pace_ratio: number | null;
	projected_end_spend: number | null;
	severity: Severity;
	alerted_at: number | null;
}

const BASE = "/api/v1/budget-pacing-alerts";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	filters: BudgetPacingAlertsListFilters = {},
): Promise<ListBudgetPacingAlertsResponse> {
	const qs = new URLSearchParams();
	if (filters.severity) qs.set("severity", filters.severity);
	if (filters.scope_type) qs.set("scope_type", filters.scope_type);
	if (filters.scope_id) qs.set("scope_id", filters.scope_id);
	if (filters.sort) qs.set("sort", filters.sort);
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBudgetPacingAlertsResponse>(res);
}

export async function upsert(
	body: UpsertBudgetPacingAlertBody,
): Promise<UpsertBudgetPacingAlertResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<UpsertBudgetPacingAlertResponse>(res);
}

export const budgetPacingAlertsQueryKeys = {
	all: ["budget-pacing-alerts"] as const,
	list: (filters?: BudgetPacingAlertsListFilters) =>
		[...budgetPacingAlertsQueryKeys.all, "list", filters ?? {}] as const,
	detail: (id: string) => [...budgetPacingAlertsQueryKeys.all, id] as const,
};
